// Forecasts are read separately from the saved commission ledger. A missing
// forecast must never turn into a zero balance or replace saved payment data.
export function mergeAffiliatePipeline(detail, forecast) {
  const buildings = new Map((detail.buildings || []).map((building) => [
    building.buildingId,
    { ...building, pending: 0, invoices: (building.invoices || []).filter((invoice) => invoice.kind !== 'pending') },
  ]));
  for (const invoice of forecast.rows || []) {
    if (!buildings.has(invoice.buildingId)) {
      buildings.set(invoice.buildingId, {
        buildingId: invoice.buildingId, buildingName: invoice.buildingName,
        earned: 0, paid: 0, owed: 0, pending: 0, invoices: [],
      });
    }
    const building = buildings.get(invoice.buildingId);
    building.pending = Math.round((building.pending + Number(invoice.amount || 0)) * 100) / 100;
    building.invoices.push({ ...invoice, kind: 'pending' });
  }
  for (const building of buildings.values()) {
    building.invoices.sort((left, right) => (
      Number(right.kind === 'pending') - Number(left.kind === 'pending')
      || String(right.invoiceDate || '').localeCompare(String(left.invoiceDate || ''))
    ));
  }
  return {
    ...detail,
    buildings: [...buildings.values()],
    totals: { ...detail.totals, pipeline: forecast.total },
    pipelineIncluded: true,
  };
}

// Keep a slow external lookup from leaving the interface waiting indefinitely.
// Caller cancellation is separate from a timeout so closing a modal stays quiet.
export async function readAffiliateJson(url, headers, signal) {
  const controller = new AbortController();
  let timedOut = false;
  const cancel = () => controller.abort();
  if (signal?.aborted) cancel();
  else signal?.addEventListener('abort', cancel, { once: true });
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 30000);
  try {
    const response = await fetch(url, { headers, signal: controller.signal });
    // A successful HTTP response is not usable until its body is complete.
    // Treat interrupted/invalid JSON as a failed read, never an empty ledger.
    const body = response.ok
      ? await response.json()
      : await response.json().catch(() => ({}));
    if (controller.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
    if (!response.ok) {
      const error = new Error(response.status === 403
        ? 'Affiliate payouts are restricted to Admin accounts.'
        : body.error || body.message || body.msg || `Request failed (${response.status})`);
      error.status = response.status;
      throw error;
    }
    return body;
  } catch (error) {
    if (timedOut) throw new Error('This request is taking too long. Please try again.');
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', cancel);
  }
}

function waitForForecast(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    const cancel = () => {
      clearTimeout(timer);
      reject(new DOMException('Cancelled', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', cancel);
      resolve();
    }, milliseconds);
    if (signal?.aborted) cancel();
    else signal?.addEventListener('abort', cancel, { once: true });
  });
}

export async function readAffiliateForecast(url, headers, signal) {
  const deadline = Date.now() + 90000;
  while (!signal?.aborted) {
    const body = await readAffiliateJson(url, headers, signal);
    if (body.pipelineIncluded === true && Number.isFinite(body.total)) return body;
    if (body.status !== 'loading' || Date.now() >= deadline) {
      throw new Error('Pending invoice amounts are unavailable. Please try again.');
    }
    await waitForForecast(Math.min(10, Math.max(2, Number(body.retryAfterSeconds) || 2)) * 1000, signal);
  }
  throw new DOMException('Cancelled', 'AbortError');
}
